
import { Calendar } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import Icon from "@/components/ui/icon";
import { useEffect, useRef } from "react";
import { VALID_CONTENT_TYPES, ContentType } from "@/utils/contentFormUtils";
import EditableNotes from "./EditableNotes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ContentMetadataPanelProps {
  contentItem: any;
  url: string;
  contentType: string;
  collectionId: string;
  notes: string;
  isSaving: boolean;
  collections: any[];
  handleUrlChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleContentTypeChange: (newType: string) => void;
  handleCollectionChange: (newCollectionId: string) => void;
  handleNotesChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleNotesSave: () => Promise<boolean>;
}

const ContentMetadataPanel = ({
  contentItem,
  url,
  contentType,
  collectionId,
  notes,
  isSaving,
  collections,
  handleUrlChange,
  handleContentTypeChange,
  handleCollectionChange,
  handleNotesChange,
  handleNotesSave,
}: ContentMetadataPanelProps) => {
  
  // Debug log for content type value
  useEffect(() => {
    console.log(`[DEBUG] ContentMetadataPanel: contentType value = "${contentType}"`, {
      contentItem_content_type: contentItem.content_type,
      state_contentType: contentType,
      valid_types: VALID_CONTENT_TYPES,
      type_in_valid_list: VALID_CONTENT_TYPES.includes(contentType.toLowerCase() as ContentType)
    });
  }, [contentType, contentItem.content_type]);
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleNotesValueChange = (newValue: string) => {
    // Create a synthetic event to maintain compatibility with existing handler
    const syntheticEvent = {
      target: {
        value: newValue
      }
    } as React.ChangeEvent<HTMLTextAreaElement>;
    
    handleNotesChange(syntheticEvent);
  };

  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label htmlFor="url">URL</Label>
          <div className="flex items-center text-sm text-clustr-midGray">
            <Icon icon={Calendar} size={14} className="mr-1" />
            <span>{formatDate(contentItem.created_at)}</span>
          </div>
        </div>
        <Input
          id="url"
          value={url}
          onChange={handleUrlChange}
          className="w-full"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="content-type">Type</Label>
        <Select
          value={contentType.toLowerCase()}
          onValueChange={(value: string) => {
            // Validate that the value is a valid ContentType before calling handler
            if (VALID_CONTENT_TYPES.includes(value as ContentType)) {
              handleContentTypeChange(value as ContentType);
            }
          }}
        >
          <SelectTrigger id="content-type" className="w-full">
            <SelectValue placeholder="Select content type" />
          </SelectTrigger>
          <SelectContent>
            {VALID_CONTENT_TYPES.map(type => (
              <SelectItem key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="collection">Collection</Label>
        <select
          id="collection"
          value={collectionId}
          onChange={(e) => handleCollectionChange(e.target.value)}
          className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">{collectionId === "" ? "In Box" : "In Box"}</option>
          {collections.map((collection: any) => (
            <option key={collection.id} value={collection.id}>
              {collection.name}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`notes-textarea-${contentItem.id}`}>Notes</Label>
        <div className="relative">
          <EditableNotes
            value={notes}
            onChange={handleNotesValueChange}
            onSave={handleNotesSave}
            placeholder="Add notes about this content"
            isLoading={isSaving}
            contentId={contentItem.id}
          />
          {isSaving && (
            <div className="absolute right-2 top-2 text-xs text-clustr-midGray animate-pulse">
              Saving...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentMetadataPanel;
